import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/16/solid";
import { useStore } from "@nanostores/react";
import pluralize from "pluralize";
import { ReactElement, useMemo } from "react";

import { useDebounce } from "../hooks/use-debounce.ts";
import { getSelectedElementsByType } from "../services/graph/get-selected-elements-by-type.ts";
import { edgeOperations } from "../services/graph/user-actions/edge-operations.ts";
import { nodeOperations } from "../services/graph/user-actions/node-operations.ts";
import {
  $selectedElements,
  $selectedItemConnections,
} from "../stores/graph/selection";
import { $cytoscapeInstance } from "../stores/graph/ui.ts";
import { $router } from "../stores/router.ts";
import { ErrorMessage } from "./catalyst/ErrorMessage.tsx";
import { Input } from "./catalyst/Input.tsx";
import { GraphConnectionsSection } from "./GraphConnectionSection.tsx";
import { GraphDetailField } from "./GraphDetailField.tsx";
import { GraphDetailText } from "./GraphDetailText.tsx";

export const GraphSelectedElementDetails = (): null | ReactElement => {
  const { incoming, outgoing } = useStore($selectedItemConnections);
  const router = useStore($router);
  const cytoscapeInstance = useStore($cytoscapeInstance);

  const selectedElements = useStore($selectedElements);
  const firstSelectedElement = selectedElements[0];

  const graphId = router?.route === "graph" ? router.params.id : null;

  // Memoize multi-selection calculations
  const multiSelectionData = useMemo(() => {
    if (selectedElements.length <= 1) return null;

    // selectedElements now automatically expands groups to their children at the store level
    const selectedElementsByType = getSelectedElementsByType(selectedElements);
    const { edges, nodes } = selectedElementsByType;

    // Count entity types
    const entityTypeCounts = nodes.reduce((map, node) => {
      if (node.entityId) {
        map.set(node.entityId, (map.get(node.entityId) || 0) + 1);
      }
      return map;
    }, new Map<string, number>());

    // Get most connected nodes
    const nodeConnectionCounts = new Map<
      string,
      { count: number; value: string }
    >();
    if (cytoscapeInstance) {
      nodes.forEach((node) => {
        const connectionCount = cytoscapeInstance
          .getElementById(node.id)
          .connectedEdges().length;
        if (connectionCount > 0) {
          nodeConnectionCounts.set(node.id, {
            count: connectionCount,
            value: node.value,
          });
        }
      });
    }

    // Sort by connection count (descending)
    const sortedNodes = Array.from(nodeConnectionCounts.entries())
      .sort(([, a], [, b]) => b.count - a.count)
      .slice(0, 5); // Top 5 most connected

    return {
      edges,
      entityTypeCounts,
      nodes,
      sortedNodes,
    };
  }, [selectedElements, cytoscapeInstance]);

  // Prepare all the data we might need, regardless of selection state
  const defaultValue =
    firstSelectedElement != null && "value" in firstSelectedElement
      ? firstSelectedElement.value
      : null;

  const name =
    firstSelectedElement != null &&
    (firstSelectedElement.type === "node" ||
      firstSelectedElement.type === "cluster")
      ? firstSelectedElement.entityName
      : undefined;

  const isNode = firstSelectedElement?.type === "node";
  const isEdge = firstSelectedElement?.type === "edge";

  const { handleChange: handleValueChange, value: localValue } = useDebounce({
    callback: (value) => {
      if (!graphId || !firstSelectedElement?.id || value == null) return;

      if (isEdge) {
        edgeOperations.update({
          edgeId: firstSelectedElement.id,
          graphId,
          newValue: value,
        });
        return;
      }

      if (isNode) {
        nodeOperations.updateNodeMeta(
          graphId,
          firstSelectedElement.id,
          "value",
          value,
        );
      }
    },
    ...(defaultValue ? { initialValue: defaultValue } : {}),
    delay: 500,
  });

  // Incoming and outgoing already have entityName from denormalized data
  const incomingWithEntityNames = incoming;
  const outgoingWithEntityNames = outgoing;

  // Handle multi-selection
  if (multiSelectionData) {
    const { edges, entityTypeCounts, nodes, sortedNodes } = multiSelectionData;

    return (
      <div className="flex h-full flex-col overflow-y-auto">
        <div className="flex-1 space-y-4 p-4">
          <GraphDetailField label="Selection Summary">
            <GraphDetailText
              text={`${nodes.length} ${pluralize("Node", nodes.length)} / ${edges.length} ${pluralize("Edge", edges.length)} Selected`}
            />
          </GraphDetailField>

          {entityTypeCounts.size > 0 && (
            <GraphDetailField label="Entity Types">
              <div className="space-y-1">
                {Array.from(entityTypeCounts.entries())
                  .sort(([, a], [, b]) => b - a)
                  .map(([entityId, count]) => {
                    // Get entity name from any node with this entityId
                    const nodeWithEntity = nodes.find(
                      (n) => n.entityId === entityId,
                    );
                    const entityName = nodeWithEntity?.entityName || entityId;
                    return (
                      <div className="text-sm text-zinc-300" key={entityId}>
                        • {count} {entityName}
                      </div>
                    );
                  })}
              </div>
            </GraphDetailField>
          )}

          {sortedNodes.length > 0 && (
            <GraphDetailField label="Most Connected">
              <div className="space-y-1">
                {sortedNodes.map(([nodeId, { count, value }]) => (
                  <div className="text-sm text-zinc-300" key={nodeId}>
                    • {value} ({count} {pluralize("connection", count)})
                  </div>
                ))}
              </div>
            </GraphDetailField>
          )}
        </div>
      </div>
    );
  }

  if (!firstSelectedElement) return null;

  return (
    <div className="grid h-full max-h-full grid-rows-3">
      <div className="overflow-y-hidden border-b border-b-zinc-800 p-4 pt-2 last:border-b-0">
        {name && (
          <GraphDetailField label="Name">
            <GraphDetailText text={name} />
          </GraphDetailField>
        )}

        <GraphDetailField label={isEdge ? "Relationship" : "Value"}>
          <Input
            className="block"
            invalid={localValue.length === 0}
            onChange={(e) => handleValueChange(e.target.value)}
            type="text"
            value={localValue}
          />
          {localValue.length === 0 && (
            <ErrorMessage>
              {isEdge ? "Relationship" : "Value"} cannot be empty
            </ErrorMessage>
          )}
        </GraphDetailField>
      </div>

      {isNode && incoming.length > 0 && (
        <GraphConnectionsSection
          icon={<ArrowRightIcon className="mr-2 h-4 w-4 text-zinc-500" />}
          items={incomingWithEntityNames}
          title={pluralize("Incoming connection", incoming.length, true)}
        />
      )}

      {isNode && outgoing.length > 0 && (
        <GraphConnectionsSection
          icon={<ArrowLeftIcon className="mr-2 h-4 w-4 text-zinc-500" />}
          items={outgoingWithEntityNames}
          title={pluralize("Outgoing connection", outgoing.length, true)}
        />
      )}

      {isEdge && (
        <div className="border-b border-b-zinc-800 p-4 last:border-b-0">
          <GraphDetailField label="Source">
            <GraphDetailText text={firstSelectedElement.source} />
          </GraphDetailField>
          <GraphDetailField label="Target">
            <GraphDetailText text={firstSelectedElement.target} />
          </GraphDetailField>
        </div>
      )}
    </div>
  );
};
